import { memo } from 'react';
import { MediaItem } from '@/types/posts';
import { ChannelPreviewData } from './types';
import { handleOf, identityChannel, initialsOf, useBrandPreviewIdentity } from './previewIdentity';
import { LinkedInPreview } from './platforms/LinkedInPreview';
import { TwitterPreview } from './platforms/TwitterPreview';
import { InstagramPreview } from './platforms/InstagramPreview';
import { FacebookPreview } from './platforms/FacebookPreview';
import { TikTokPreview } from './platforms/TikTokPreview';
import { ThreadsPreview } from './platforms/ThreadsPreview';
import { BlueskyPreview } from './platforms/BlueskyPreview';
import { PinterestPreview } from './platforms/PinterestPreview';
import { YouTubePreview } from './platforms/YouTubePreview';
import { GoogleBusinessPreview } from './platforms/GoogleBusinessPreview';

/**
 * Platform Preview Router - maps platform string to component
 */
export const PlatformPreviewRouter = memo(
  ({
    platform,
    caption,
    media,
    isCarouselMode,
    channels,
    activeIndex,
    onIndexChange,
  }: {
    platform: string;
    caption: string;
    media: MediaItem[];
    isCarouselMode: boolean;
    channels?: ChannelPreviewData[];
    activeIndex?: number;
    onIndexChange?: (index: number) => void;
  }) => {
    // Find matching channel for this platform; otherwise speak as the brand.
    const identity = useBrandPreviewIdentity();
    const found =
      channels?.find((ch) => ch.platform === platform.toLowerCase()) ??
      identityChannel(platform.toLowerCase(), identity);
    // A real channel can lack a name or username: never render a blank identity.
    const shownName = found.displayName || identity.name;
    // A handle is one token: a display name that leaked in as a handle ("Brightline Coffee")
    // is slugified, a real username only loses its leading "@".
    const rawHandle = (found.handle || '').trim().replace(/^@+/, '');
    const channel: ChannelPreviewData = {
      ...found,
      displayName: shownName,
      handle: rawHandle && !/\s/.test(rawHandle) ? rawHandle : handleOf(rawHandle || shownName),
      initials: found.initials ?? initialsOf(shownName),
    };
    const props = { caption, media, isCarousel: isCarouselMode, channel, activeIndex, onIndexChange };

    switch (platform.toLowerCase()) {
      case 'linkedin':
        return <LinkedInPreview {...props} />;
      case 'twitter':
        return <TwitterPreview {...props} />;
      case 'instagram':
        return <InstagramPreview {...props} />;
      case 'facebook':
        return <FacebookPreview {...props} />;
      case 'tiktok':
        return <TikTokPreview {...props} />;
      case 'threads':
        return <ThreadsPreview {...props} />;
      case 'bluesky':
        return <BlueskyPreview {...props} />;
      case 'pinterest':
        return <PinterestPreview {...props} />;
      case 'youtube':
        return <YouTubePreview {...props} />;
      case 'google_my_business':
        return <GoogleBusinessPreview {...props} />;
      default:
        return <LinkedInPreview {...props} />;
    }
  }
);
PlatformPreviewRouter.displayName = 'PlatformPreviewRouter';
